import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/seo";
import { Card, Pill } from "@/components/ui/Primitives";
import { Sunrise, TrendingUp, TrendingDown, Users, Home, Wrench, DollarSign, ArrowRight, BarChart3, Headphones, Loader2, Pause, Play, Download } from "lucide-react";

const fmtCAD = (cents) => `CA$${((cents || 0) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

export default function Briefing() {
  usePageTitle("Daily briefing — Central Rentals");
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/briefing/daily")
      .then((r) => setBriefing(r.data))
      .catch(() => setBriefing(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading your briefing…</div>;
  if (!briefing) return (
    <div className="p-6 text-center text-sm text-slate-500">
      Couldn&apos;t load today&apos;s briefing. Try refreshing in a moment.
    </div>
  );

  const s = briefing.snapshot || {};
  const bench = briefing.benchmarks || {};

  return (
    <div className="space-y-6" data-testid="briefing-page">
      {/* Header */}
      <div className="rounded-2xl bg-slate-900 text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 text-emerald-300 text-xs font-bold tracking-[0.18em] uppercase mb-2">
            <Sunrise className="w-4 h-4" /> Daily briefing · {briefing.date}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-1">Portfolio at a glance</h1>
          <p className="text-slate-300 text-sm max-w-xl">
            Live view of what needs your attention today. {(() => {
              try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "your local time";
                const d = new Date(); d.setHours(7, 0, 0, 0);
                const t = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(d);
                return `Emailed to you at ${t} (${tz}) — this is the interactive version.`;
              } catch (_) {
                return "Emailed to you every morning — this is the interactive version.";
              }
            })()}
          </p>
        </div>
      </div>

      <AudioBriefingCard/>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="briefing-kpis">
        <KpiCard icon={DollarSign} label="Rent this month"
                 value={fmtCAD(s.rent_collected_month_cents)}
                 sub={`${fmtPct(s.collection_rate_pct)} of expected`} />
        <KpiCard icon={Home} label="Occupancy"
                 value={fmtPct(s.occupancy_pct)}
                 sub={`${s.active_leases}/${s.units} units`}
                 tone={s.vacant_units > 0 ? "amber" : "emerald"} />
        <KpiCard icon={Wrench} label="Open tickets"
                 value={s.open_tickets}
                 sub={s.stale_tickets_48h > 0 ? `${s.stale_tickets_48h} stale 48h+` : "All fresh"}
                 tone={s.stale_tickets_48h > 0 ? "rose" : "emerald"} />
        <KpiCard icon={Users} label="Arrears"
                 value={fmtCAD(s.overdue_rent_cents)}
                 sub={`${s.overdue_tenants} tenant(s)`}
                 tone={s.overdue_tenants > 0 ? "rose" : "emerald"} />
      </div>

      {/* Priorities */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-slate-900">Today&apos;s priorities</h2>
          {briefing.priorities?.length > 0 && (
            <span className="text-xs text-slate-500">{briefing.priorities.length} item(s)</span>
          )}
        </div>
        {briefing.priorities?.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">No urgent items — a rare calm day.</div>
        ) : (
          <div className="space-y-2" data-testid="briefing-priorities">
            {briefing.priorities.map((p, i) => (
              <PriorityRow key={i} p={p} />
            ))}
          </div>
        )}
      </Card>

      {/* Benchmarking */}
      <Card className="p-5" data-testid="briefing-benchmarks">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-4 h-4 text-emerald-700" />
          <h2 className="font-display text-lg font-bold text-slate-900">How you compare</h2>
        </div>
        <div className="text-xs text-slate-500 mb-3">
          {bench.peer_group ? `${bench.peer_group} · ${bench.peer_count} anonymised peers` : "Not enough peer data yet"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <BenchRow label="Occupancy" data={bench.occupancy} />
          <BenchRow label="STP rate" data={bench.stp_rate} />
          <BenchRow label="Collection rate" data={bench.collection_rate} />
        </div>
        <div className="text-[11px] text-slate-400 mt-4">{bench.note}</div>
      </Card>

      {/* Bills coming due */}
      {s.bills_due_7d?.length > 0 && (
        <Card className="p-5" data-testid="briefing-bills-week">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold text-slate-900">Bills due this week</h2>
            <Link to="/bills" className="text-xs font-semibold text-emerald-700 hover:underline">
              Open bills →
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {s.bills_due_7d.map((b) => (
              <div key={b.bill_id} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-slate-900">{b.vendor_name}</div>
                  <div className="text-xs text-slate-500">Due {b.due_date}</div>
                </div>
                <div className="font-mono font-semibold text-slate-900">{fmtCAD(b.amount_cents)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "slate" }) {
  const border =
    tone === "rose" ? "border-rose-200 bg-rose-50" :
    tone === "amber" ? "border-amber-200 bg-amber-50" :
    tone === "emerald" ? "border-emerald-200 bg-emerald-50" :
    "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border ${border} p-4`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold font-mono text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function PriorityRow({ p }) {
  const tone = p.urgency === "high" ? "rose" : p.urgency === "med" ? "amber" : "emerald";
  const border = tone === "rose" ? "border-l-rose-500" : tone === "amber" ? "border-l-amber-500" : "border-l-emerald-500";
  return (
    <div className={`bg-slate-50 border-l-4 ${border} rounded-r-lg p-3.5`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Pill tone={tone}>{p.urgency}</Pill>
            <h3 className="font-semibold text-slate-900 text-sm">{p.title}</h3>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">{p.detail}</p>
        </div>
        {p.cta && p.route && (
          <Link to={p.route}
            className="text-xs font-semibold text-slate-900 hover:text-emerald-700 whitespace-nowrap inline-flex items-center gap-1">
            {p.cta} <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    </div>
  );
}

function BenchRow({ label, data }) {
  const you = data?.your_pct;
  const median = data?.median_pct;
  const percentile = data?.percentile;
  const better = you !== null && median !== null && you !== undefined && median !== undefined && you >= median;
  return (
    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="text-xl font-bold font-mono text-slate-900 mt-1">{fmtPct(you)}</div>
      <div className="text-xs text-slate-600 flex items-center gap-1 mt-1">
        {you !== null && median !== null && (
          better ? <TrendingUp className="w-3 h-3 text-emerald-600" /> : <TrendingDown className="w-3 h-3 text-rose-600" />
        )}
        peer median: <strong>{fmtPct(median)}</strong>
      </div>
      {percentile !== null && percentile !== undefined && (
        <div className="text-[11px] text-slate-500 mt-0.5">Top {100 - percentile}% of peers</div>
      )}
    </div>
  );
}


function AudioBriefingCard() {
  const [voice, setVoice] = useState("sage");
  const [state, setState] = useState({ audio: null, script: "", notice: "", loading: false, playing: false });
  const audioRef = useRef(null);

  const load = async (force = false) => {
    setState((s) => ({ ...s, loading: true, notice: "" }));
    try {
      const { data } = await api.get(`/briefing/daily/audio`, {
        params: {
          voice,
          // Natural human cadence — the default 1.0 came across too rapid
          // for a morning briefing. 0.9 keeps every word crisp without
          // sounding sluggish.
          speed: 0.9,
          force: force ? "true" : undefined,
        },
      });
      setState({
        audio: data.audio_base64 ? `data:audio/mpeg;base64,${data.audio_base64}` : null,
        script: data.script || "",
        notice: data.notice || "",
        loading: false,
        playing: false,
      });
    } catch (err) {
      setState((s) => ({ ...s, loading: false,
        notice: err?.response?.data?.detail || "Audio unavailable right now. Try again in a moment." }));
    }
  };

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (state.playing) { el.pause(); }
    else { el.play().catch(() => {}); }
  };

  const download = () => {
    if (!state.audio) return;
    const a = document.createElement("a");
    a.href = state.audio;
    a.download = `central-rentals-briefing-${new Date().toISOString().slice(0, 10)}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Card className="p-5 md:p-6 border-slate-200" data-testid="audio-briefing-card">
      <div className="flex flex-wrap items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-none">
          <Headphones className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Podcast · new</span>
            <Pill tone="slate" className="text-[10px]">~90 sec</Pill>
          </div>
          <div className="font-display text-lg md:text-xl font-bold text-slate-900 mt-1">Listen to today&apos;s briefing</div>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
            A short audio version of your briefing — good for driving, walking, or between meetings.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {!state.audio && !state.loading && (
              <button onClick={() => load(false)} data-testid="audio-briefing-generate"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-lg shadow-emerald-500/20 transition-colors">
                <Play className="w-4 h-4"/> Generate audio briefing
              </button>
            )}
            {state.loading && (
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium">
                <Loader2 className="w-4 h-4 animate-spin"/> Generating audio…
              </div>
            )}
            {state.audio && (
              <>
                <button onClick={toggle} data-testid="audio-briefing-toggle"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold transition-colors">
                  {state.playing ? <><Pause className="w-4 h-4"/> Pause</> : <><Play className="w-4 h-4"/> Play</>}
                </button>
                <button onClick={download} data-testid="audio-briefing-download"
                  className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold transition-colors">
                  <Download className="w-4 h-4"/> MP3
                </button>
                <audio
                  ref={audioRef}
                  src={state.audio}
                  onPlay={() => setState((s) => ({ ...s, playing: true }))}
                  onPause={() => setState((s) => ({ ...s, playing: false }))}
                  onEnded={() => setState((s) => ({ ...s, playing: false }))}
                  className="hidden"
                  data-testid="audio-briefing-el"
                />
              </>
            )}
            <select
              value={voice}
              onChange={(e) => { setVoice(e.target.value); setState((s) => ({ ...s, audio: null })); }}
              disabled={state.loading}
              data-testid="audio-briefing-voice"
              className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none disabled:opacity-50">
              <option value="sage">Sage · warm, natural (recommended)</option>
              <option value="coral">Coral · friendly female</option>
              <option value="ash">Ash · natural male</option>
              <option value="alloy">Alloy · neutral conversational</option>
              <option value="nova">Nova · upbeat female</option>
              <option value="onyx">Onyx · deep male</option>
              <option value="fable">Fable · British storyteller</option>
              <option value="shimmer">Shimmer · bright female</option>
              <option value="echo">Echo · calm male</option>
            </select>
            {state.audio && (
              <button onClick={() => load(true)} disabled={state.loading}
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 ml-1">
                Regenerate
              </button>
            )}
          </div>

          {state.notice && (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="audio-briefing-notice">
              {state.notice}
            </div>
          )}
          {state.script && (
            <details className="mt-4 group">
              <summary className="text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer select-none">
                Show transcript
              </summary>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{state.script}</p>
            </details>
          )}
        </div>
      </div>
    </Card>
  );
}
